import Button from "../common/Button";
import BaseModal from "../modals/BaseModal";

function FieldError({ message }) {
  if (!message) {
    return null;
  }

  return (
    <div
      className="muted-text"
      style={{ color: "#b91c1c", fontSize: "12px" }}
      role="alert"
    >
      {message}
    </div>
  );
}

export default function StartCallModal({
  open,
  title,
  form,
  busy,
  isPhoneEditable,
  fieldErrors,
  submitError,
  successData,
  runtimeConfigs,
  runtimeConfigsLoading,
  runtimeConfigsError,
  onChange,
  onSubmit,
  onClose,
}) {
  const modalMeta =
    title === "Live Call"
      ? "Enter the call details to start a manual outbound call."
      : "Review the call details before starting the outbound call.";

  return (
    <BaseModal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      meta={modalMeta}
      modalId="startCallModal"
    >
      {successData ? (
        <>
          <div className={`confirmation-tag ${successData.status === "failed" ? "" : "success"}`.trim()}>
            {successData.status === "failed"
              ? "Call request was recorded, but ARI did not accept it."
              : "Call start accepted."}
          </div>
          <div className="modal-summary-block">
            <div className="modal-summary-label">Call Details</div>
            <div className="modal-summary-text" style={{ display: "grid", gap: "6px" }}>
              <div>Call ID: {successData.call_id || "Not provided"}</div>
              <div>Attempt ID: {successData.attempt_id || "Not provided"}</div>
              <div>Status: {successData.status || "Not provided"}</div>
              <div>Phone: {successData.phone || form.phone || "Not provided"}</div>
              {successData.ari?.message ? (
                <div>ARI: {successData.ari.message}</div>
              ) : null}
            </div>
          </div>
          <div className="outbound-actions">
            <Button variant="green" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <form className="outbound-form" onSubmit={onSubmit}>
          {submitError ? (
            <div
              className="confirmation-tag"
              style={{
                marginTop: 0,
                borderColor: "rgba(220, 38, 38, 0.2)",
                background: "rgba(220, 38, 38, 0.08)",
                color: "#991b1b",
              }}
              role="alert"
            >
              {submitError}
            </div>
          ) : null}

          <div className="outbound-field">
            <label className="outbound-label" htmlFor="callVoiceProvider">
              Voice Provider
            </label>
            <select
              className="outbound-input"
              id="callVoiceProvider"
              name="voice_provider"
              value={form.voice_provider || "openai"}
              onChange={(event) => onChange("voice_provider", event.target.value)}
              disabled={busy}
            >
              <option value="openai">OpenAI Voice (default)</option>
              <option value="elevenlabs">ElevenLabs — Indian English (demo)</option>
            </select>
            {(form.voice_provider || "openai") === "elevenlabs" ? (
              <div className="muted-text" style={{ fontSize: "12px" }}>
                Natural ElevenLabs voice demo — only a phone number is required.
              </div>
            ) : null}
          </div>

          <div className="outbound-field">
            <label className="outbound-label" htmlFor="callRuntimeConfig">
              AI Runtime Configuration
            </label>
            <select
              className="outbound-input"
              id="callRuntimeConfig"
              name="ai_config_id"
              value={form.ai_config_id || ""}
              onChange={(event) => onChange("ai_config_id", event.target.value)}
              disabled={busy || runtimeConfigsLoading}
            >
              <option value="">
                {runtimeConfigsLoading
                  ? "Loading active configurations..."
                  : runtimeConfigs?.length
                    ? "Select active configuration"
                    : "Use automatic runtime resolution"}
              </option>
              {(runtimeConfigs || []).map((config) => (
                <option key={config.id} value={config.id}>
                  {config.company_name
                    ? `${config.name} - ${config.company_name}`
                    : config.name}
                </option>
              ))}
            </select>
            {runtimeConfigsError ? (
              <div className="muted-text" style={{ color: "#92400e", fontSize: "12px" }}>
                {runtimeConfigsError}
              </div>
            ) : runtimeConfigs?.length ? (
              <div className="muted-text" style={{ fontSize: "12px" }}>
                One active configuration is used at a time per client or default scope.
              </div>
            ) : (
              <div className="muted-text" style={{ fontSize: "12px" }}>
                No active configuration is published yet. The call will use runtime fallback resolution.
              </div>
            )}
            <FieldError message={fieldErrors.ai_config_id} />
          </div>

          <div className="outbound-field">
            <label className="outbound-label" htmlFor="callPhone">
              Phone Number
            </label>
            <input
              className="outbound-input"
              id="callPhone"
              name="phone"
              type="tel"
              placeholder="+919444233942"
              value={form.phone}
              onChange={(event) => onChange("phone", event.target.value)}
              disabled={busy}
              readOnly={!isPhoneEditable}
            />
            <FieldError message={fieldErrors.phone} />
          </div>

          <div className="outbound-field">
            <label className="outbound-label" htmlFor="callPatientName">
              Contact Name
            </label>
            <input
              className="outbound-input"
              id="callPatientName"
              name="name"
              type="text"
              placeholder="Contact name"
              value={form.name}
              onChange={(event) => onChange("name", event.target.value)}
              disabled={busy}
            />
            <FieldError message={fieldErrors.name} />
          </div>

          <div className="outbound-field">
            <label className="outbound-label" htmlFor="callReason">
              Reason
            </label>
            <input
              className="outbound-input"
              id="callReason"
              name="reason"
              type="text"
              placeholder="e.g. Call for our service"
              value={form.reason}
              onChange={(event) => onChange("reason", event.target.value)}
              disabled={busy}
            />
            <FieldError message={fieldErrors.reason} />
          </div>

          <div className="outbound-actions">
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="green" disabled={busy}>
              {busy ? "Calling..." : "Start Call"}
            </Button>
          </div>
        </form>
      )}
    </BaseModal>
  );
}
